import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { apiAgent } from "../../../axiosConfig";
import { Avatar } from "../../../design_system/components/Avatar/Avatar.jsx";
import { Icons } from "../../../design_system/components/Icons";
import { useToast } from "../../../design_system/components/Toast/ToastContext.jsx";
import { Loader } from "../../../design_system/components/Loader.jsx";
import ConditionalRender from "../../../ConditionalRender.js";
import { useSettingsContext, settingsSnapshot } from "./SettingsContext";
import {
    NOTIFICATION_POST_TYPES,
    buildTypeSettings,
    findInvalidMentionedFor,
    toApiPayload,
} from "../../utils/AllNotificationOptions";
import NotificationTypeCard from "./_NotificationTypeCard.jsx";

const memberSnapshot = (members) =>
    settingsSnapshot(members.map(m => ({ id: m.id, notification_settings: m.notification_settings })));

export const TeamNotifications = (props) => {
    const [members, setMembers] = useState([]);
    const [mentionedForTags, setMentionedForTags] = useState({}); // per-type map
    const [enabledTypes, setEnabledTypes] = useState({});
    const [expandedMemberId, setExpandedMemberId] = useState(null);
    const [expandedCard, setExpandedCard] = useState({}); // { [memberId]: type }
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const current_user = useSelector(s => s.current_user.user);
    const current_team = useSelector(s => s.current_team.team);
    const toast = useToast();
    const { registerTab, unregisterTab, setHasChanges } = useSettingsContext();
    const snapshotRef = useRef(null);

    useEffect(() => {
        props.setActiveSettingTab("team_notifications");
        apiAgent({
            method: "get",
            url: "/team_members/get_notifications",
            params: { approval_status: "accepted" },
        })
            .then((res) => {
                const built = (res.data.team_users || []).map(m => ({
                    ...m,
                    notification_settings: buildTypeSettings(m.notification_settings),
                }));
                setMembers(built);
                setMentionedForTags(res.data.mentioned_for_tags || {});
                setEnabledTypes(res.data.enabled_post_types || {});
                snapshotRef.current = memberSnapshot(built);
                setIsDataLoaded(true);
            })
            .catch(() => {
                toast.error("Couldn't load team notification settings");
                setIsDataLoaded(true);
            });
    }, []);

    // ── Change detection ─────────────────────────────────────────────────────
    useEffect(() => {
        if (snapshotRef.current === null) return;
        setHasChanges(memberSnapshot(members) !== snapshotRef.current);
    }, [members, setHasChanges]);

    const handleMemberCardChange = (memberId, type, nextSetting) => {
        setMembers(prev => prev.map(m =>
            m.id === memberId
                ? { ...m, notification_settings: { ...m.notification_settings, [type]: nextSetting } }
                : m
        ));
    };

    const onSubmit = useCallback(() => {
        const offender = members.find(m => findInvalidMentionedFor(m.notification_settings));
        if (offender) {
            toast.error(`${offender.name || offender.email}: select at least one mention category, or turn off the filter.`);
            return Promise.reject(new Error("invalid_mentioned_for"));
        }
        return apiAgent({
            method: "post",
            url: "/team_members/update_notifications",
            data: { team_users: members.map(m => ({ id: m.id, notification_settings: toApiPayload(m.notification_settings) })) },
        }).then((res) => {
            const failed = (res.data && res.data.failed_ids) || [];
            if (failed.length > 0) {
                const names = members
                    .filter(m => failed.includes(String(m.id)))
                    .map(m => m.name || m.email)
                    .join(", ");
                toast.warning(`Couldn't save settings for: ${names}`);
                return;
            }
            toast.success("Changes saved");
            snapshotRef.current = memberSnapshot(members);
            setHasChanges(false);
        }).catch(() => {
            toast.warning("Notification preferences can't be updated");
        });
    }, [members, toast, setHasChanges]);

    const handleCancel = useCallback(() => {
        if (!snapshotRef.current) return;
        const restored = JSON.parse(snapshotRef.current); // [{id, notification_settings}]
        const byId = {};
        restored.forEach(r => { byId[r.id] = r.notification_settings; });
        setMembers(prev => prev.map(m => ({ ...m, notification_settings: byId[m.id] || m.notification_settings })));
    }, []);

    const submitRef = useRef(onSubmit);
    const cancelRef = useRef(handleCancel);
    submitRef.current = onSubmit;
    cancelRef.current = handleCancel;

    useEffect(() => {
        const id = registerTab({
            onSave: (...args) => submitRef.current(...args),
            onCancel: (...args) => cancelRef.current(...args),
        });
        return () => unregisterTab(id);
    }, [registerTab, unregisterTab]);

    // Hide post-type cards the team isn't enabled for (Q24/§2H).
    const visibleTypes = NOTIFICATION_POST_TYPES.filter(({ type }) => {
        if (type === "Post::SecFiling") return current_team?.sec_filings_enabled;
        return !(type in enabledTypes) || enabledTypes[type];
    });

    return (
        <div className="settings-panel">
            <div className="settings-panel__header">
                <div className="settings-panel__title body-l-bold">Team Notifications</div>
                <div className="settings-panel__description body-s-semibold">Manage email notification settings for your team members. Configure how and when they receive updates.</div>
            </div>

            {!isDataLoaded ? <Loader isLoading Size="Medium" /> :
                <div className="ntf-team">
                    <div className="ntf-team__head body-m-semibold">Name &amp; Current Settings</div>
                    {members.map((member) => (
                        <ConditionalRender key={member.id} if={current_user.is_publisher || !member.is_super_member}>
                            <div className={`ntf-member${expandedMemberId === member.id ? " ntf-member--expanded" : ""}`}>
                                <button
                                    type="button"
                                    className="ntf-member__head"
                                    onClick={() => setExpandedMemberId(expandedMemberId === member.id ? null : member.id)}
                                >
                                    <Avatar name={member.name || member.email} width={40} />
                                    <span className="ntf-member__id">
                                        <span className="ntf-member__person">
                                            <span className="ntf-member__name body-s-bold">{member.name}</span>
                                            <span className="ntf-member__email body-s">{member.email}</span>
                                        </span>
                                        <span className="ntf-member__pills">
                                            {visibleTypes.map(tc => (
                                                <span key={tc.type} className="ntf-pill">
                                                    <span className="ntf-pill__type support-m-semibold">{tc.shortLabel}</span>
                                                    <span className="ntf-pill__value support-m-semibold">
                                                        {member.notification_settings[tc.type]?.frequency}
                                                    </span>
                                                </span>
                                            ))}
                                        </span>
                                    </span>
                                    <Icons name={expandedMemberId === member.id ? "chevron-up" : "chevron-down"} size={20} />
                                </button>

                                {expandedMemberId === member.id && (
                                    <div className="ntf-member__body ntf-cards">
                                        {visibleTypes.map(tc => (
                                            <NotificationTypeCard
                                                key={tc.type}
                                                typeConfig={tc}
                                                setting={member.notification_settings[tc.type]}
                                                mentionedForTags={mentionedForTags[tc.type] || []}
                                                expanded={expandedCard[member.id] === tc.type}
                                                onToggle={() => setExpandedCard(prev => ({
                                                    ...prev,
                                                    [member.id]: prev[member.id] === tc.type ? null : tc.type,
                                                }))}
                                                onChange={(s) => handleMemberCardChange(member.id, tc.type, s)}
                                                groupKey={member.id}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </ConditionalRender>
                    ))}
                </div>}
        </div>
    );
};

export default TeamNotifications;
